'use client';

import React from 'react';

import CheckoutHeader from 'corgi/checkout-header/components/CheckoutHeader';
import CommonDrawer from 'corgi/common-drawer/components/CommonDrawer';
import CommonUspTeaser from 'corgi/common-usp-teaser/components/CommonUspTeaser';
import ErrorBoundary from 'corgi/common/components/ErrorBoundary';
import { PageGridItem } from 'corgi/common/components/PageGrid';
import RouterProvider from 'corgi/common/providers/RouterProvider';
import { WishlistProvider } from 'corgi/common/providers/WishlistProvider';
import FooterLinkSection from 'corgi/footer-link-section/components/FooterLinkSection';
import GlobalHead from 'corgi/global-head/components/GlobalHead';

import { LogoutBanner } from '@components/common/fragments/AutoLogoutBanner';
import Footer from '@components/common/fragments/Footer';
import Header from '@components/common/fragments/Header';
import { GTMScripts } from '@components/common/scripts/GTM';
import type { SignupProps } from '@local-types/signup/props';
import { arePropsNonEmpty } from '@utils/render';
import { useAppRouter } from '@utils/router/use-router';
import { isDigitalCheckout, isRegularOrDigitalCheckoutFlow } from '@utils/signup/user-flow';
import ErrorPage from '../../app/error';

interface SignupPageProps extends SignupProps {
  id: string;
  children: React.ReactNode;
}

const FullPageStyle = () => (
  <style>
    {`
      body {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }
    `}
  </style>
);

export default function SignupPage({
  id,
  pageMeta,
  signupPageProps,
  globalNavProps,
  children,
}: SignupPageProps) {
  const router = useAppRouter();

  const { userFlow } = pageMeta;
  const {
    dataLayer: { initialDataLayer, events },
    globalHeadProps,
    wishlistState,
    uspTeaserProps,
    checkoutHeaderProps,
    checkoutFooterProps,
  } = signupPageProps;
  const { stage, shouldInjectGtmScript } = pageMeta;

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <WishlistProvider state={wishlistState}>
        <RouterProvider router={router}>
          <GlobalHead {...globalHeadProps} />
          <GTMScripts
            id={id}
            dataLayer={initialDataLayer}
            events={events}
            stage={stage}
            withScripts={shouldInjectGtmScript}
          />
          {isDigitalCheckout(userFlow) && <FullPageStyle />}

          <LogoutBanner />
          <CommonDrawer />

          {isRegularOrDigitalCheckoutFlow(userFlow) ? (
            <CheckoutHeader {...checkoutHeaderProps} />
          ) : (
            <Header {...globalNavProps} />
          )}

          <main>{children}</main>

          {isRegularOrDigitalCheckoutFlow(userFlow) ? (
            <PageGridItem as="footer" mt="auto">
              {arePropsNonEmpty(uspTeaserProps) && <CommonUspTeaser {...uspTeaserProps} />}
              <FooterLinkSection {...checkoutFooterProps} />
            </PageGridItem>
          ) : (
            <Footer {...globalNavProps} uspTeaserProps={uspTeaserProps} />
          )}
        </RouterProvider>
      </WishlistProvider>
    </ErrorBoundary>
  );
}
